import { useState, useEffect, useRef } from 'react';
import { Database, Search, Lightbulb, ShieldCheck, Type, Send } from 'lucide-react';

interface StepConfig {
  icon: React.ElementType;
  label: string;
  messages: string[];
}

const STEPS: StepConfig[] = [
  {
    icon: Database,
    label: 'Gathering Info',
    messages: [
      'Loading your agent settings...',
      'Fetching sales materials...',
      'Pulling WhatsApp templates...',
    ],
  },
  {
    icon: Search,
    label: 'Studying Info',
    messages: [
      'Analyzing prospect data...',
      'Reviewing lead profile...',
      'Preparing context...',
    ],
  },
  {
    icon: Lightbulb,
    label: 'Preparing a Strategy',
    messages: [
      'Building conversation approach...',
      'Selecting best opening...',
      'Personalizing the pitch...',
    ],
  },
  {
    icon: ShieldCheck,
    label: 'Verifying All',
    messages: [
      'Running final checks...',
      'Validating message format...',
      'Confirming WhatsApp connection...',
    ],
  },
  {
    icon: Type,
    label: 'Typing a Message',
    messages: [
      'Crafting your WhatsApp message...',
      'Personalizing the content...',
      'Adding finishing touches...',
    ],
  },
  {
    icon: Send,
    label: 'Sending a Message',
    messages: [
      'Delivering via WhatsApp...',
      'Connecting to messaging service...',
      'Message on its way...',
    ],
  },
];

const STEP_INTERVAL_MS = 1500;
const MESSAGE_INTERVAL_MS = 1000;

interface WhatsAppProgressStepsProps {
  apiDone: boolean;
  apiFailed: boolean;
}

export function WhatsAppProgressSteps({ apiDone, apiFailed }: WhatsAppProgressStepsProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedViaApi = useRef(false);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setActiveIndex(prev => {
          if (prev < STEPS.length - 1) return prev + 1;
          return prev;
        });
        setFading(false);
      }, 300);
    }, STEP_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setMessageIndex(prev => prev + 1), MESSAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (apiDone && !completedViaApi.current) {
      completedViaApi.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      setFading(true);
      setTimeout(() => {
        setActiveIndex(STEPS.length - 1);
        setFading(false);
      }, 300);
    }
  }, [apiDone]);

  useEffect(() => {
    if (apiFailed) {
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [apiFailed]);

  const step = STEPS[activeIndex];
  const Icon = step.icon;
  const currentMessage = step.messages[messageIndex % step.messages.length];

  return (
    <div className="py-6 flex flex-col items-center">
      <div className={`flex flex-col items-center transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1875FF]/15 to-[#05E0D1]/15 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#1875FF] animate-[pulse_1.8s_ease-in-out_infinite]" />
          <div className="absolute inset-0 rounded-2xl bg-[#1875FF]/10 animate-ping" style={{ animationDuration: '2s' }} />
        </div>

        <p className="text-base font-semibold text-gray-900">{step.label}</p>
        <p className="text-sm text-gray-400 mt-1 h-5">{currentMessage}</p>
      </div>

      <div className="flex items-center gap-2 mt-8">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i < activeIndex
                ? 'w-1.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                : i === activeIndex
                  ? 'w-6 bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                  : 'w-1.5 bg-gray-200'
            }`}
          />
        ))}
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">
        Usually takes about 10 seconds
      </p>
    </div>
  );
}
